import ExpandableContainer from '../../../../../components/ExpandableContainer';
import { Sidebar } from '../Sidebar';
import { Table } from '../Table';
import PdfViewer from '../PdfViewer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Flex, LoadingOverlay, Tabs, Text } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import extractionStore from '../../../store';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-hot-toast';

export function generateAlphabetColumn(columnNumber) {
  let column = '';
  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return column;
}

export const getColumns = (value, rowCount) => {
  return [
    ...Object.keys(value || {}).map((_, index) => ({
      columnId: index + 2,
      resizable: true,
      width: 100
    })),
    ...[...Array(rowCount).keys()].map((_, index) => ({
      columnId: index + Object.keys(value).length + 1,
      resizable: true,
      width: 100
    }))
  ];
};

const useStyles = createStyles(() => ({
  table: {
    backgroundColor: '#fff',
    borderRadius: '4px'
  }
}));

function Extraction({ context }) {
  const {
    width,
    isLoading,
    hasChanges,
    openPopup,
    isStructured,
    selectedFund,
    onSelectFund,
    selectedLookupTable,
    onSelectTable,
    onChangeAction,
    action,
    pickedFund,
    onPickFund,
    onPickChildFund,
    pickedChildFund,
    onEnableValue,
    activeFundChildValue,
    onDeleteSelectedCell,
    categories,
    funds,
    onChangeCategory,
    category,
    refetchCategories,
    onSearch,
    setPickedFund,
    setSelectedFund,
    error,
    onSelectColumn,
    selectedColumn,
    isLookup,
    onClearValuesColumn,
    onClearFundValues,
    onFocusLocation,
    onDeleteRow,
    sheetId,
    tableName,
    fundMappings,
    refetchFundMappings,
    refetchSheet,
    rowPinning,
    columnPinning,
    setWidth,
    headerIndex,
    footerIndex,
    onSelectionUnStructuredCells,
    onSelectionStructuredCells,
    data,
    zoomLevel,
    onFocusedCell,
    foundCell,
    onReplicateAutoExtract,
    pdfViewMode,
    onChangePdfViewMode,
    isTransformation,
    onFullSelectionStructuredCells,
    periodId,
    isLoadingAutoExtract,
    isIndividualFund,
    headerComponent,
    openCustomTableDrawer,
    openUpdateCustomTableDrawer,
    onSelectCustomTable,
    fileId,
    fileFormat,
    sheetName,
    pdfRegionSelections,
    tableCsv,
    onSavePdfRegionSelection,
    onDeletePdfRegionSelection,
    isSavingPdfRegionSelection,
    isDeletingPdfRegionSelection,
    isLoadingPdfRegionPreview
  } = context;

  const { isLockedPeriod, extractionType } = extractionStore;

  const rows = useMemo(() => {
    return data?.map((item, index) => {
      const newItem = {
        1: `${index + 1}`
      };

      Object.values(item).forEach((value, i) => {
        newItem[i + 2] = value;
      });

      return newItem;
    });
  }, [data]);

  const { isSidebarExpanded } = extractionStore;
  const sidebarWidth = useMemo(() => {
    if (!isSidebarExpanded) return width;

    const expandedTarget = Math.min(Math.round(window.innerWidth * 0.94), 1200);
    return Math.max(width, expandedTarget);
  }, [isSidebarExpanded, width]);

  const rowCount = useMemo(() => {
    if (!data || !data?.[0]) return 0;
    return Math.round((window.innerWidth - sidebarWidth) / 100) - Object.keys(data?.[0])?.length > 0
      ? Math.round((window.innerWidth - sidebarWidth) / 100) - Object.keys(data?.[0]).length
      : 0;
  }, [data, sidebarWidth]);

  const fundLookUp = useMemo(() => {
    const _fundLookup = {};

    if (isLookup) {
      funds?.forEach(fund =>
        fund.columns?.forEach(column =>
          column.values?.forEach(value => {
            const key = `${value.rowId + 1}-${value.columnId}`;
            _fundLookup[key] = fund;
          })
        )
      );
    } else {
      funds?.forEach(fund =>
        fund.tables?.forEach(table =>
          table.columns.forEach(column =>
            column.values.forEach(value => {
              const key = `${value.rowId + 1}-${value.columnId}`;
              _fundLookup[key] = fund;
            })
          )
        )
      );
    }
    return _fundLookup;
  }, [funds, isLookup]);

  const hasOnlyOneMappedFund = useMemo(() => {
    return fundMappings?.fund_mappings?.length === 1;
  }, [fundMappings]);

  // Keep `_getCellStyle`'s identity stable for the lifetime of the
  // component. canvas-datagrid invokes this fn per draw, so reading
  // `selectedFund` / `fundLookUp` from a ref still reflects the latest
  // values — but the function reference no longer churns on every
  // MobX-observer re-render. That stops `columns` from being recomputed
  // (and the canvas grid from being torn down) when an unrelated sidebar
  // interaction (e.g. typing a prompt) re-renders the page.
  const cellStyleStateRef = useRef({ selectedFund, fundLookUp });
  useEffect(() => {
    cellStyleStateRef.current = { selectedFund, fundLookUp };
  });

  const _getCellStyle = useCallback((rowId, colId) => {
    const { selectedFund: currentSelected, fundLookUp: currentLookup } = cellStyleStateRef.current;
    const fund = currentLookup[`${rowId}-${colId}`];

    // Show the fund-color highlight for any extracted cell that belongs to
    // a selected fund — including cells outside the structured-table header
    // / footer band. The header / footer lines are still rendered separately
    // by the canvas; we just don't gate the cell background on them.
    if (fund && currentSelected.includes(fund.id)) {
      return {
        hasSelectedValues: true,
        background: fund.backgroundColor
      };
    }

    return {
      background: '#fff'
    };
  }, []);

  const columns = useMemo(() => {
    const result = getColumns(data?.[0], rowCount)?.map(column => ({
      ...column,
      accessorKey: `${column.columnId}`,
      header: generateAlphabetColumn(column.columnId - 1),
      size: column.width,
      cell: ({ row, getValue, column }) => {
        const value = getValue(row.index);
        const rowId = row.index + 1;
        const customStyle = _getCellStyle(rowId, column.getIndex() + 1);
        // const customStyle = {};
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              // backgroundColor: !isTransformation && isLine && '#CDE0FF',
              // cursor: isLine && 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '0 4px',
              ...customStyle
            }}
          >
            {value}
          </div>
        );
      }
    }));

    return [
      {
        columnId: 1,
        accessorKey: `1`,
        header: '№',
        size: 40
      },
      ...result
    ];
    // `_getCellStyle` is intentionally omitted — it's now identity-stable
    // (ref-backed) so it never changes; including it would only cause
    // false-positive re-memoization warnings to be silenced.
  }, [data, rowCount, headerIndex, footerIndex, isTransformation, _getCellStyle]);

  const handleSelection = useCallback(
    (cells, cellType, col) => {
      if (action === 'Automatic') return;
      if (isLockedPeriod) return toast.error('You cannot select cells in a locked period');

      if (cells.length > 0) {
        if (isStructured && cells[0].rowId === headerIndex) {
          const startIndex = headerIndex || 0;
          const endIndex = footerIndex || data?.length;
          const _formatedValues = data?.slice(startIndex, endIndex)?.map((item, idx) => {
            const value = item[cells[0].columnId - 1];

            const _payload = {
              rowId: idx + startIndex + 1,
              columnId: cells[0].columnId,
              text: value
            };

            return _payload;
          });

          onSelectionStructuredCells(_formatedValues, cellType, col);
        } else {
          const mutatedCells = cells?.map(cell => ({
            columnId: cell.columnId,
            rowId: cell.rowId,
            text: cell.text
          }));

          onSelectionUnStructuredCells(mutatedCells, cellType);
        }
      }
      return cells;
    },
    [
      funds,
      data,
      onSelectionUnStructuredCells,
      onSelectionStructuredCells,
      isStructured,
      headerIndex,
      footerIndex,
      isLookup,
      action,
      isLockedPeriod
    ]
  );

  const hasFundMapping = useMemo(() => {
    return !!fundMappings?.fund_mappings?.find(el => el.id && el.source_fund);
  }, [fundMappings?.fund_mappings]);

  const { classes } = useStyles();

  return (
    <Flex id="grid-table-extraction-root" gap="12px" w="100%">
      <ExpandableContainer
        id="grid-table-extraction-sidebar-container"
        width={sidebarWidth}
        setWidth={setWidth}
        initWidth={300}
        maxWidth={1200}
      >
        <Sidebar
          isIndividualFund={isIndividualFund || hasOnlyOneMappedFund}
          selectedLookupTable={selectedLookupTable}
          onSelectTable={onSelectTable}
          isLoadingAutoExtract={isLoadingAutoExtract}
          hasChanges={hasChanges}
          openPopup={openPopup}
          isStructured={isStructured}
          selectedFund={selectedFund}
          onSelectFund={onSelectFund}
          onChangeAction={onChangeAction}
          action={action}
          pickedFund={pickedFund}
          onPickFund={onPickFund}
          onPickChildFund={onPickChildFund}
          pickedChildFund={pickedChildFund}
          onEnableValue={onEnableValue}
          activeFundChildValue={activeFundChildValue}
          onDeleteSelectedCell={onDeleteSelectedCell}
          categories={categories}
          funds={funds}
          refetchCategories={refetchCategories}
          onChangeCategory={onChangeCategory}
          category={category}
          isLoaging={isLoading}
          onSearch={onSearch}
          setPickedFund={setPickedFund}
          setSelectedFund={setSelectedFund}
          error={error}
          onSelectColumn={onSelectColumn}
          selectedColumn={selectedColumn}
          isLookup={isLookup}
          onClearValuesColumn={onClearValuesColumn}
          onClearFundValues={onClearFundValues}
          onFocusLocation={onFocusLocation}
          onDeleteRow={onDeleteRow}
          sheetId={sheetId}
          tableName={tableName}
          refetchFundMappings={refetchFundMappings}
          refetchSheet={refetchSheet}
          footerIndex={footerIndex}
          headerIndex={headerIndex}
          isLockedPeriod={isLockedPeriod}
          handleSelection={handleSelection}
          onReplicateAutoExtract={onReplicateAutoExtract}
          disableLookupOptionCategory={hasFundMapping}
          onFullSelectionStructuredCells={onFullSelectionStructuredCells}
          openCustomTableDrawer={openCustomTableDrawer}
          periodId={periodId}
          onSelectCustomTable={onSelectCustomTable}
          extractionType={extractionType}
          openUpdateCustomTableDrawer={openUpdateCustomTableDrawer}
        />
      </ExpandableContainer>

      <Box
        id="grid-table-extraction-table-shell"
        p="16px"
        style={{
          width: `calc(100% - ${sidebarWidth}px)`,
          height: `100%`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflowY: 'hidden',
          overflowX: 'hidden'
        }}
        pos="relative"
        className={classes.table}
      >
        <LoadingOverlay id="grid-table-extraction-loading" visible={isLoading} zIndex={1000} />

        {!isLoading && fileFormat === 'pdf' && (
          <>
            <Flex align="end" justify="space-between" gap="12px" mb="12px" wrap="wrap">
              <Text fz="13px" c="dimmed">
                {pdfRegionSelections?.length
                  ? `${pdfRegionSelections.length} region${pdfRegionSelections.length > 1 ? 's' : ''} saved for this PDF page selection workflow.`
                  : 'No regions saved yet. Draw one or more table regions in the PDF tab.'}
              </Text>

              {pdfViewMode === 'table' && tableCsv && (
                <Button
                  id="grid-table-extraction-download-csv"
                  variant="default"
                  size="xs"
                  onClick={() => {
                    const blob = new Blob([tableCsv], {
                      type: 'text/csv;charset=utf-8;'
                    });
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement('a');
                    anchor.href = url;
                    anchor.download = 'pdf-regions.csv';
                    anchor.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download CSV
                </Button>
              )}
            </Flex>

            <Tabs
              id="grid-table-extraction-tabs"
              value={pdfViewMode}
              onChange={value => onChangePdfViewMode(value || 'pdf')}
              mb="12px"
            >
              <Tabs.List>
                <Tabs.Tab id="grid-table-extraction-tab-table" value="table">
                  Table
                </Tabs.Tab>
                <Tabs.Tab id="grid-table-extraction-tab-pdf" value="pdf">
                  PDF
                </Tabs.Tab>
              </Tabs.List>
            </Tabs>

            {pdfViewMode === 'pdf' && (
              <Box
                data-testid="pdf-viewer-panel"
                style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
              >
                <PdfViewer
                  periodId={periodId}
                  fileId={fileId}
                  sheetName={sheetName}
                  selections={pdfRegionSelections}
                  onSaveSelection={onSavePdfRegionSelection}
                  onDeleteSelection={onDeletePdfRegionSelection}
                  isSavingSelection={isSavingPdfRegionSelection}
                  isDeletingSelection={isDeletingPdfRegionSelection}
                  onShowTable={() => onChangePdfViewMode('table')}
                />
              </Box>
            )}

            {pdfViewMode === 'table' && (
              <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <LoadingOverlay visible={isLoadingPdfRegionPreview} zIndex={1000} />
                {data?.length ? (
                  <>
                    {headerComponent}
                    <Table
                      key={'canvas-extraction' + category?.name + isSidebarExpanded}
                      className="canvas-extraction"
                      getCellStyle={_getCellStyle}
                      data={data}
                      rows={rows}
                      columns={columns}
                      foundCell={foundCell}
                      rowPinning={rowPinning}
                      columnPinning={columnPinning}
                      isStructured={isStructured}
                      zoomLevel={zoomLevel}
                      handleSelection={handleSelection}
                      headerIndex={headerIndex}
                      footerIndex={footerIndex}
                      onFocusedCell={onFocusedCell}
                      width={sidebarWidth}
                    />
                  </>
                ) : (
                  <Text c="dimmed" fz="14px">
                    {pdfRegionSelections?.length
                      ? 'No table data could be extracted from the saved PDF regions.'
                      : 'Select one or more table regions in the PDF tab to generate the Table view.'}
                  </Text>
                )}
              </Box>
            )}
          </>
        )}

        {!isLoading && fileFormat !== 'pdf' && data?.length && (
          <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {headerComponent}
            <Table
              key={'canvas-extraction' + category?.name + isSidebarExpanded}
              className="canvas-extraction"
              getCellStyle={_getCellStyle}
              data={data}
              rows={rows}
              columns={columns}
              foundCell={foundCell}
              rowPinning={rowPinning}
              columnPinning={columnPinning}
              isStructured={isStructured}
              zoomLevel={zoomLevel}
              handleSelection={handleSelection}
              headerIndex={headerIndex}
              footerIndex={footerIndex}
              onFocusedCell={onFocusedCell}
              width={sidebarWidth}
            />
          </Box>
        )}
      </Box>
    </Flex>
  );
}

export default observer(Extraction);
